import type {
  CalendarDaily,
  Market,
  MonthlySales,
  SalesInfo,
  SalesMarket,
  SalesProduct,
  SalesStore,
  SettleInfo,
} from '@/types/statistics';

const MARKET_ICON_URLS = {
  smartstore:
    'https://cplat-images.s3.ap-northeast-2.amazonaws.com/sellerbox/market-icon/IconMarketSmartstore.png',
  coupang:
    'https://cplat-images.s3.ap-northeast-2.amazonaws.com/sellerbox/market-icon/IconMarketCoupang.png',
  elevenStreet:
    'https://cplat-images.s3.ap-northeast-2.amazonaws.com/sellerbox/market-icon/IconMarket11st.png',
  auction:
    'https://cplat-images.s3.ap-northeast-2.amazonaws.com/sellerbox/market-icon/IconMarketAuction.png',
  zigzag:
    'https://cplat-images.s3.ap-northeast-2.amazonaws.com/sellerbox/market-icon/IconMarketZigzag.png',
} as const;

const PREVIEW_SOURCE_MONTH = '2026-04';

const createMembershipStatisticsPreviewEndDate = () => {
  const endDate = new Date();
  endDate.setHours(0, 0, 0, 0);
  return endDate;
};

const formatDateKey = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatMonthKey = (date: Date) => formatDateKey(date).slice(0, 7);

const parseMonthKey = (monthKey: string) => new Date(`${monthKey}-01T00:00:00+09:00`);

const getMonthDistance = (startMonthKey: string, endMonthKey: string) => {
  const startDate = parseMonthKey(startMonthKey);
  const endDate = parseMonthKey(endMonthKey);

  return (
    (endDate.getFullYear() - startDate.getFullYear()) * 12 +
    (endDate.getMonth() - startDate.getMonth())
  );
};

const addMonthsToMonthKey = (monthKey: string, months: number) => {
  const date = parseMonthKey(monthKey);
  date.setMonth(date.getMonth() + months);

  return formatMonthKey(date);
};

const getDaysInMonth = (monthKey: string) => {
  const [year, month] = monthKey.split('-').map(Number);

  return new Date(year, month, 0).getDate();
};

const previewEndDate = createMembershipStatisticsPreviewEndDate();
const previewMonthKey = formatMonthKey(previewEndDate);
const previewDayOfMonth = previewEndDate.getDate();
const previewMonthDistance = getMonthDistance(PREVIEW_SOURCE_MONTH, previewMonthKey);

const mapMonthKeyToPreview = (monthKey: string) =>
  addMonthsToMonthKey(monthKey, previewMonthDistance);

const createCalendarDaily = (
  day: number,
  salesAmount: number,
  settleAmount: number,
): CalendarDaily => ({
  Date: `${PREVIEW_SOURCE_MONTH}-${String(day).padStart(2, '0')}T00:00:00`,
  SalesAmount: salesAmount,
  SalesCount: Math.max(1, Math.round(salesAmount / 25_000)),
  SalesDiscountAmount: 0,
  SalesShippingFee: 0,
  SettleCompletionAmount: settleAmount,
  SettleYetAmount: 0,
});

const createCalendarDate = (month: string, day: number) =>
  `${month}-${String(day).padStart(2, '0')}T00:00:00`;

const salesInfo = {
  TotalSalesAmount: 16_954_940,
  TotalSalesCount: 246,
  TotalSalesDiscountAmount: -645_000,
  TotalSalesShippingFee: 784_500,
} satisfies SalesInfo;

const settleInfo = {
  TotalSettleAmount: 15_384_910,
  TotalSettleCompletionAmount: 13_814_880,
  TotalSettleYetAmount: 1_570_030,
} satisfies SettleInfo;

const sourceMonthlySalesList = [
  {
    FirstDayOfMonth: '2025-10-01T00:00:00',
    Month: '2025-10',
    SalesAmount: 3_600_000,
    SalesAmountUntilDay: 3_600_000,
  },
  {
    FirstDayOfMonth: '2025-11-01T00:00:00',
    Month: '2025-11',
    SalesAmount: 3_450_000,
    SalesAmountUntilDay: 3_450_000,
  },
  {
    FirstDayOfMonth: '2025-12-01T00:00:00',
    Month: '2025-12',
    SalesAmount: 4_000_000,
    SalesAmountUntilDay: 4_000_000,
  },
  {
    FirstDayOfMonth: '2026-01-01T00:00:00',
    Month: '2026-01',
    SalesAmount: 4_600_000,
    SalesAmountUntilDay: 4_600_000,
  },
  {
    FirstDayOfMonth: '2026-02-01T00:00:00',
    Month: '2026-02',
    SalesAmount: 3_700_000,
    SalesAmountUntilDay: 3_700_000,
  },
  {
    FirstDayOfMonth: '2026-03-01T00:00:00',
    Month: '2026-03',
    SalesAmount: 3_350_504,
    SalesAmountUntilDay: 3_350_504,
  },
  {
    FirstDayOfMonth: '2026-04-01T00:00:00',
    Month: '2026-04',
    SalesAmount: 3_644_040,
    SalesAmountUntilDay: 3_644_040,
  },
] satisfies MonthlySales[];

const monthlySalesList = sourceMonthlySalesList.map((item) => {
  const month = mapMonthKeyToPreview(item.Month);

  return {
    ...item,
    FirstDayOfMonth: `${month}-01T00:00:00`,
    Month: month,
  };
});

const sourceCalendarDailyList = [
  createCalendarDaily(1, 851_620, 927_800),
  createCalendarDaily(2, 792_400, 744_000),
  createCalendarDaily(3, 827_700, 710_000),
  createCalendarDaily(4, 827_720, 710_710),
  createCalendarDaily(5, 790_300, 511_810),
  createCalendarDaily(6, 745_080, 563_400),
  createCalendarDaily(7, 901_650, 923_400),
  createCalendarDaily(8, 806_060, 709_090),
  createCalendarDaily(9, 618_700, 604_080),
  createCalendarDaily(10, 901_740, 805_110),
  createCalendarDaily(11, 743_230, 569_030),
  createCalendarDaily(12, 839_080, 522_080),
  createCalendarDaily(13, 563_100, 435_000),
  createCalendarDaily(14, 729_200, 763_000),
  createCalendarDaily(15, 756_180, 828_790),
  createCalendarDaily(16, 572_000, 820_300),
  createCalendarDaily(17, 851_590, 524_200),
  createCalendarDaily(18, 948_470, 792_920),
  createCalendarDaily(19, 970_300, 828_000),
  createCalendarDaily(20, 824_390, 783_560),
  createCalendarDaily(21, 674_070, 584_800),
  createCalendarDaily(22, 420_360, 723_830),
] satisfies CalendarDaily[];

const dailySalesInfo = {
  TotalSalesAmount: 420_360,
  TotalSalesCount: 17,
  TotalSalesDiscountAmount: 0,
  TotalSalesShippingFee: 0,
} satisfies SalesInfo;

const dailySettleInfo = {
  TotalSettleAmount: 723_830,
  TotalSettleCompletionAmount: 723_830,
  TotalSettleYetAmount: 0,
} satisfies SettleInfo;

const dailyMarketList = [
  {
    IsConnectedOff: false,
    MarketIconUrl: MARKET_ICON_URLS.smartstore,
    MarketName: '스마트스토어',
    SalesAmount: 243_000,
    SalesCount: 9,
    SalesDiscountAmount: 0,
    SalesShippingFee: 0,
    SettleAmount: 430_210,
    StoreName: '셀러박스마켓',
  },
  {
    IsConnectedOff: false,
    MarketIconUrl: MARKET_ICON_URLS.coupang,
    MarketName: '쿠팡',
    SalesAmount: 123_000,
    SalesCount: 6,
    SalesDiscountAmount: 0,
    SalesShippingFee: 0,
    SettleAmount: 201_400,
    StoreName: '헤이즐마켓',
  },
  {
    IsConnectedOff: false,
    MarketIconUrl: MARKET_ICON_URLS.elevenStreet,
    MarketName: '11번가',
    SalesAmount: 54_360,
    SalesCount: 2,
    SalesDiscountAmount: 0,
    SalesShippingFee: 0,
    SettleAmount: 92_220,
    StoreName: '제인마켓',
  },
] satisfies Market[];

type DailyStatisticsMock = {
  dailyMarketList: Market[];
  dailySalesInfo: SalesInfo;
  dailySettleInfo: SettleInfo;
};

const dailyMarketDistribution = [
  {
    IsConnectedOff: false,
    MarketIconUrl: MARKET_ICON_URLS.smartstore,
    MarketName: '스마트스토어',
    StoreName: '셀러박스마켓',
    ratio: 0.58,
  },
  {
    IsConnectedOff: false,
    MarketIconUrl: MARKET_ICON_URLS.coupang,
    MarketName: '쿠팡',
    StoreName: '헤이즐마켓',
    ratio: 0.29,
  },
  {
    IsConnectedOff: false,
    MarketIconUrl: MARKET_ICON_URLS.elevenStreet,
    MarketName: '11번가',
    StoreName: '제인마켓',
    ratio: 0.13,
  },
] as const;

const splitByRatio = (total: number, ratios: readonly number[]) => {
  let allocated = 0;

  return ratios.map((ratio, index) => {
    if (index === ratios.length - 1) return total - allocated;

    const value = Math.max(0, Math.round(total * ratio));
    allocated += value;

    return value;
  });
};

export const getMembershipStatisticsCalendarMock = (
  monthlySales: MonthlySales,
): CalendarDaily[] => {
  if (!monthlySales.Month) return [];

  const dayCount =
    monthlySales.Month === previewMonthKey ? previewDayOfMonth : getDaysInMonth(monthlySales.Month);
  const previewSourceCalendarDailyList = Array.from(
    { length: dayCount },
    (_, index) => sourceCalendarDailyList[index % sourceCalendarDailyList.length],
  );
  const sourceSalesAmount = previewSourceCalendarDailyList.reduce(
    (sum, item) => sum + item.SalesAmount,
    0,
  );
  const ratio = sourceSalesAmount > 0 ? monthlySales.SalesAmount / sourceSalesAmount : 0;

  return previewSourceCalendarDailyList.map((item, index) => {
    const salesAmount = Math.round(item.SalesAmount * ratio);
    const settleCompletionAmount = Math.round(item.SettleCompletionAmount * ratio);

    return {
      ...item,
      Date: createCalendarDate(monthlySales.Month, index + 1),
      SalesAmount: salesAmount,
      SalesCount: Math.max(1, Math.round(item.SalesCount * ratio)),
      SettleCompletionAmount: settleCompletionAmount,
      SettleYetAmount: 0,
    };
  });
};

const calendarDailyList = getMembershipStatisticsCalendarMock(
  monthlySalesList[monthlySalesList.length - 1] ??
    sourceMonthlySalesList[sourceMonthlySalesList.length - 1],
);

const createPreviewMonthlySales = (monthKey: string): MonthlySales => {
  const matchingMonthlySales = monthlySalesList.find((item) => item.Month === monthKey);
  if (matchingMonthlySales) return matchingMonthlySales;

  const fallbackMonthlySales =
    monthlySalesList[monthlySalesList.length - 1] ??
    sourceMonthlySalesList[sourceMonthlySalesList.length - 1] ??
    MonthlySalesInitial;

  return {
    ...fallbackMonthlySales,
    FirstDayOfMonth: `${monthKey}-01T00:00:00`,
    Month: monthKey,
  };
};

const findPreviewCalendarDaily = (
  selectedDate: string,
  previewCalendarDailyList: CalendarDaily[],
) => {
  const selectedDateKey = selectedDate.slice(0, 10);
  const currentDaily = previewCalendarDailyList.find(
    (item) => item.Date.slice(0, 10) === selectedDateKey,
  );
  if (currentDaily) return currentDaily;

  const selectedMonthKey = selectedDateKey.slice(0, 7);
  const selectedMonthCalendarDailyList = getMembershipStatisticsCalendarMock(
    createPreviewMonthlySales(selectedMonthKey),
  );

  return selectedMonthCalendarDailyList.find((item) => item.Date.slice(0, 10) === selectedDateKey);
};

const emptyDailyStatisticsMock = {
  dailyMarketList: [],
  dailySalesInfo: {
    TotalSalesAmount: 0,
    TotalSalesCount: 0,
    TotalSalesDiscountAmount: 0,
    TotalSalesShippingFee: 0,
  },
  dailySettleInfo: {
    TotalSettleAmount: 0,
    TotalSettleCompletionAmount: 0,
    TotalSettleYetAmount: 0,
  },
} satisfies DailyStatisticsMock;

export const getMembershipStatisticsDailyMock = (
  selectedDate: string,
  previewCalendarDailyList = calendarDailyList,
): DailyStatisticsMock => {
  const currentDaily = findPreviewCalendarDaily(selectedDate, previewCalendarDailyList);

  if (!currentDaily) return emptyDailyStatisticsMock;

  const ratios = dailyMarketDistribution.map((item) => item.ratio);
  const salesAmounts = splitByRatio(currentDaily.SalesAmount, ratios);
  const salesCounts = splitByRatio(currentDaily.SalesCount, ratios);
  const settleAmounts = splitByRatio(
    currentDaily.SettleCompletionAmount + currentDaily.SettleYetAmount,
    ratios,
  );

  return {
    dailyMarketList: dailyMarketDistribution.map((item, index) => ({
      IsConnectedOff: item.IsConnectedOff,
      MarketIconUrl: item.MarketIconUrl,
      MarketName: item.MarketName,
      SalesAmount: salesAmounts[index],
      SalesCount: salesCounts[index],
      SalesDiscountAmount: 0,
      SalesShippingFee: 0,
      SettleAmount: settleAmounts[index],
      StoreName: item.StoreName,
    })),
    dailySalesInfo: {
      TotalSalesAmount: currentDaily.SalesAmount,
      TotalSalesCount: currentDaily.SalesCount,
      TotalSalesDiscountAmount: currentDaily.SalesDiscountAmount,
      TotalSalesShippingFee: currentDaily.SalesShippingFee,
    },
    dailySettleInfo: {
      TotalSettleAmount: currentDaily.SettleCompletionAmount + currentDaily.SettleYetAmount,
      TotalSettleCompletionAmount: currentDaily.SettleCompletionAmount,
      TotalSettleYetAmount: currentDaily.SettleYetAmount,
    },
  };
};

const salesStoreList = [
  {
    MarketIconUrl: MARKET_ICON_URLS.smartstore,
    MarketName: '스마트스토어',
    SalesAmount: 2_304_120,
    SalesCount: 142,
    StoreName: '셀러박스마켓',
  },
  {
    MarketIconUrl: MARKET_ICON_URLS.coupang,
    MarketName: '쿠팡',
    SalesAmount: 623_800,
    SalesCount: 48,
    StoreName: '헤이즐마켓',
  },
  {
    MarketIconUrl: MARKET_ICON_URLS.elevenStreet,
    MarketName: '11번가',
    SalesAmount: 395_980,
    SalesCount: 32,
    StoreName: '제인마켓',
  },
  {
    MarketIconUrl: MARKET_ICON_URLS.zigzag,
    MarketName: '지그재그',
    SalesAmount: 320_140,
    SalesCount: 24,
    StoreName: '루나마켓',
  },
] satisfies SalesStore[];

const salesMarketList = [
  {
    MarketIconUrl: MARKET_ICON_URLS.smartstore,
    MarketName: '스마트스토어',
    SalesAmount: 2_372_870,
    SalesCount: 128,
  },
  {
    MarketIconUrl: MARKET_ICON_URLS.coupang,
    MarketName: '쿠팡',
    SalesAmount: 393_556,
    SalesCount: 42,
  },
  {
    MarketIconUrl: MARKET_ICON_URLS.elevenStreet,
    MarketName: '11번가',
    SalesAmount: 149_406,
    SalesCount: 18,
  },
  {
    MarketIconUrl: MARKET_ICON_URLS.zigzag,
    MarketName: '지그재그',
    SalesAmount: 98_389,
    SalesCount: 11,
  },
  {
    MarketIconUrl: MARKET_ICON_URLS.auction,
    MarketName: '옥션',
    SalesAmount: 629_819,
    SalesCount: 47,
  },
] satisfies SalesMarket[];

const salesProductList = [
  {
    MarketIconUrl: MARKET_ICON_URLS.smartstore,
    MarketName: '셀러박스마켓',
    ProductName: '무소음 무선 옵티컬 마우스',
    ProductOptionName: '블랙 / 무소음',
    SalesAmount: 65_000,
    SalesCount: 287,
    StoreName: '셀러박스마켓',
  },
  {
    MarketIconUrl: MARKET_ICON_URLS.smartstore,
    MarketName: '셀러박스마켓',
    ProductName: '메모리폼 키보드 손목 받침대',
    ProductOptionName: '베이지 / 대형',
    SalesAmount: 13_000,
    SalesCount: 213,
    StoreName: '셀러박스마켓',
  },
  {
    MarketIconUrl: MARKET_ICON_URLS.smartstore,
    MarketName: '셀러박스마켓',
    ProductName: '4구 멀티탭 USB 2포트 1.5m',
    ProductOptionName: '화이트 1.5m',
    SalesAmount: 4_460,
    SalesCount: 182,
    StoreName: '셀러박스마켓',
  },
  {
    MarketIconUrl: MARKET_ICON_URLS.coupang,
    MarketName: '헤이즐마켓',
    ProductName: '알루미늄 태블릿 거치대 PRO',
    ProductOptionName: '스페이스그레이',
    SalesAmount: 37_200,
    SalesCount: 156,
    StoreName: '헤이즐마켓',
  },
  {
    MarketIconUrl: MARKET_ICON_URLS.smartstore,
    MarketName: '셀러박스마켓',
    ProductName: '프리미엄 무선 이어폰 X9 Pro',
    ProductOptionName: '블랙',
    SalesAmount: 29_800,
    SalesCount: 129,
    StoreName: '셀러박스마켓',
  },
  {
    MarketIconUrl: MARKET_ICON_URLS.coupang,
    MarketName: '헤이즐마켓',
    ProductName: '스마트 LED 스탠드 조명 S3',
    ProductOptionName: '화이트 / 2개입',
    SalesAmount: 22_500,
    SalesCount: 117,
    StoreName: '헤이즐마켓',
  },
  {
    MarketIconUrl: MARKET_ICON_URLS.coupang,
    MarketName: '헤이즐마켓',
    ProductName: '접이식 알루미늄 노트북 거치대',
    ProductOptionName: '실버 M사이즈',
    SalesAmount: 18_400,
    SalesCount: 85,
    StoreName: '헤이즐마켓',
  },
  {
    MarketIconUrl: MARKET_ICON_URLS.elevenStreet,
    MarketName: '제인마켓',
    ProductName: '고속충전 USB-C 케이블 2m',
    ProductOptionName: '화이트',
    SalesAmount: 15_300,
    SalesCount: 52,
    StoreName: '제인마켓',
  },
  {
    MarketIconUrl: MARKET_ICON_URLS.zigzag,
    MarketName: '루나마켓',
    ProductName: '블루투스 5.0 포터블 스피커',
    ProductOptionName: '네이비',
    SalesAmount: 10_900,
    SalesCount: 31,
    StoreName: '루나마켓',
  },
  {
    MarketIconUrl: MARKET_ICON_URLS.elevenStreet,
    MarketName: '제인마켓',
    ProductName: '아이폰15 Pro 강화유리 필름',
    ProductOptionName: '아이폰15 Pro용',
    SalesAmount: 7_600,
    SalesCount: 23,
    StoreName: '제인마켓',
  },
] satisfies SalesProduct[];

export const membershipStatisticsMock = {
  selectedDate: formatDateKey(previewEndDate),
  salesInfo,
  settleInfo,
  calendarDailyList,
  monthlySalesList,
  salesMarketList,
  salesProductList,
  salesStoreList,
  dailyMarketList,
  dailySalesInfo,
  dailySettleInfo,
  salesDiffAmount: 293_530,
};
